import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { validateRef } from "../lib/referrals";
import { api } from "../lib/api";

export default function Refer() {
  const [code, setCode] = React.useState("");
  const [savedCode, setSavedCode] = React.useState("");
  const [copied, setCopied] = React.useState("");
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    const v = localStorage.getItem("rl_ref_code_v1") || "";
    setCode(v);
    setSavedCode(v);
  }, []);

  const note = "For privacy, avoid personal details in your referral code (for example, your full real name).";

  const onSave = () => {
    const c = (code || "").trim().toLowerCase();
    if (!validateRef(c)) {
      alert("Pick a code 3-20 chars, a-z, 0-9, dash or underscore.");
      return;
    }
    localStorage.setItem("rl_ref_code_v1", c);
    setSavedCode(c);
    fetchCount(c);
  };

  const link = React.useMemo(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    return savedCode ? `${base}/?ref=${savedCode}` : "";
  }, [savedCode]);

  const fetchCount = async (c) => {
    if (!validateRef(c)) return;
    try {
      const resp = await api.get(`/referrals/count`, { params: { ref: c } });
      setCount(resp?.data?.count || 0);
    } catch (e) {
      setCount(0);
    }
  };

  React.useEffect(() => {
    if (savedCode) fetchCount(savedCode);
  }, [savedCode]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied("Copied!");
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      alert("Copy failed—please copy manually.");
    }
  };

  const shareText = (t) => {
    const l = link;
    return `${t} ${l}`.trim();
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10 space-y-6">
      {/* Hero */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="refer-page-heading">Your impact matters. Invite others to begin their recovery journey.</h1>
        <p className="text-sm text-slate-500" data-testid="refer-page-subheading">Each invite can help someone take a meaningful first step.</p>
      </div>

      {/* Counter */}
      <Card>
        <CardContent className="py-4 flex items-center justify-between">
          <div className="text-slate-700 dark:text-slate-300" data-testid="refer-page-count-label-start">People who joined via your link:</div>
          <div className="text-3xl font-semibold text-teal-500" data-testid="refer-page-count-value">{count}</div>
        </CardContent>
      </Card>

      {/* Referral setup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-slate-100">Your referral link</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-xs text-amber-600" data-testid="refer-page-privacy-note">{note}</div>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1 w-full">
              <label htmlFor="code" className="text-sm font-medium">Choose a nickname code</label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. hopeful-journey"
                className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="refer-code-input"
              />
            </div>
            <Button onClick={onSave} className="whitespace-nowrap" data-testid="refer-save-code-button">Save code</Button>
          </div>

          {savedCode && (
            <div className="space-y-2">
              <div className="text-sm">Your link</div>
              <div className="flex items-center gap-2">
                <input readOnly value={link} className="flex-1 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm" data-testid="refer-link-input" />
                <Button onClick={() => copy(link)} data-testid="refer-copy-link-button">Copy</Button>
                {copied && <span className="text-xs text-teal-600" data-testid="refer-copy-feedback">{copied}</span>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pre-written messages */}
      {savedCode && (
        <Card>
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-slate-100">Choose a message to copy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              "Hey — I found Somemas Recovery and thought it might help. It offers support and guidance for people who are struggling. Here’s the link: [Your Site Link]",
              "I wanted to share this in case it helps. Somemas Recovery is a supportive recovery platform. Here’s the link: [Your Site Link]",
              "Recovery is possible. If someone needs support, you can share this: [Your Site Link]",
            ].map((msg, idx) => {
              const filled = (msg || "").replace("[Your Site Link]", link);
              return (
                <div key={idx} className="p-3 border border-slate-200 dark:border-slate-700 rounded-md flex items-start gap-3">
                  <div className="flex-1 text-sm text-slate-800 dark:text-slate-200" data-testid={`refer-template-text-${idx}`}>{filled}</div>
                  <Button variant="secondary" onClick={() => copy(filled)} data-testid={`refer-template-copy-button-${idx}`}>Copy</Button>
                </div>
              );
            })}
            {copied && <div className="text-xs text-teal-600">{copied}</div>}
          </CardContent>
        </Card>
      )}

      {/* Encouragement */}
      <Card>
        <CardContent className="py-4">
          <div className="text-sm text-slate-600 dark:text-slate-300">Thanks for helping others find recovery support! Your invitations make a real difference.</div>
        </CardContent>
      </Card>
    </div>
  );
}